import { useState } from 'react';
import { Dialog, Flex, Button, Separator } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { useGetVaultCategories } from '../../data';
import {
  useCreateVaultCategoryMutation,
  useUpdateVaultCategoryMutation,
  useDeleteVaultCategoryMutation,
} from './useVaultCategoryMutation';
import { CategoryListItem } from './CategoryListItem.jsx';
import { CategoryForm } from './CategoryForm.jsx';
import styles from './manage_categories.module.css';

export const ManageCategoriesModal = ({ open, onOpenChange }) => {
  const { t } = useTranslation('vault');
  const { data } = useGetVaultCategories();
  const categories = data.categories;

  const {
    createCategoryMutation,
    isCreating: isCreateLoading,
  } = useCreateVaultCategoryMutation();

  const {
    updateCategoryMutation,
    isUpdating,
  } = useUpdateVaultCategoryMutation();

  const {
    deleteCategoryMutation,
    isDeleting,
  } = useDeleteVaultCategoryMutation();

  const isSubmitting = isCreateLoading || isUpdating || isDeleting;

  const [editingCategory, setEditingCategory] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const showingForm = isCreating || editingCategory;

  const handleCreate = async ({ name, icon }) => {
    await createCategoryMutation({ name, icon });
    setIsCreating(false);
  };

  const handleUpdate = async ({ name, icon }) => {
    await updateCategoryMutation({
      categoryId: editingCategory.id,
      name,
      icon,
    });
    setEditingCategory(null);
  };

  const handleDelete = async categoryId => {
    await deleteCategoryMutation({ categoryId });
  };

  const handleFormCancel = () => {
    setEditingCategory(null);
    setIsCreating(false);
  };

  const handleClose = isOpen => {
    if (!isOpen) {
      setEditingCategory(null);
      setIsCreating(false);
    }
    onOpenChange(isOpen);
  };

  const title = showingForm
    ? editingCategory
      ? t('editCategory')
      : t('addCategory')
    : t('manageCategories');

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title mt="0" mb="4" size="4">
          {title}
        </Dialog.Title>

        {showingForm ? (
          <CategoryForm
            category={editingCategory}
            onSave={editingCategory ? handleUpdate : handleCreate}
            onCancel={handleFormCancel}
            isSubmitting={isSubmitting}
          />
        ) : (
          <Flex direction="column" gap="3">
            <div className={styles.categoryList}>
              {categories.map(category => (
                <CategoryListItem
                  key={category.id}
                  category={category}
                  onEdit={setEditingCategory}
                  onDelete={handleDelete}
                  isUpdating={isSubmitting}
                />
              ))}
            </div>

            <Separator size="4" />

            <Flex gap="3" mt="2" justify="end">
              <Button
                size="3"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t('close')}
              </Button>
              <Button
                size="3"
                variant="solid"
                onClick={() => setIsCreating(true)}
              >
                <i className="fa-solid fa-plus" />
                {t('addCategory')}
              </Button>
            </Flex>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};
